import React, {useEffect, useState, useRef} from "react";
import {
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    Chip,
} from "@mui/material";
import axiosInstant from "../../api";
import toast from "react-hot-toast";
import {DotLottleLoader} from "../../components/Loader";

interface ContentType {
    id: number;
    name: string;
    expert_connect?: boolean;
}

interface Props {
    brandId: number;
    onSelectionChange?: (selectedTypeIds: number[]) => void;
}

// Per-brand content type selection. Channels are no longer configured per
// brand/content type — all generic content channels apply to every content
// type, so this panel is a plain content-type checklist.
export function BrandDetailContentTypesPanel({
    brandId,
    onSelectionChange,
}: Props) {
    const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedTypeIds, setSelectedTypeIds] = useState<number[]>([]);
    const [isRmn, setIsRmn] = useState<boolean>(true);

    const fetchContentTypes = async () => {
        try {
            const response = await axiosInstant.get(`/content-types`);
            const data = Array.isArray(response.data)
                ? response.data
                : response.data?.rows || [];
            setContentTypes(data.filter((ct: any) => ct.expert_connect));
        } catch (err) {
            console.error("Failed to fetch content types:", err);
            toast.error("Failed to fetch content types.");
        }
    };

    const fetchBrand = async () => {
        try {
            const response = await axiosInstant.get(`/brands/${brandId}`);
            const brand = response.data;
            setIsRmn(!!brand?.rmn);

            const brandContentTypeIds = Array.isArray(brand?.content_types)
                ? brand.content_types.map((ct: any) => ct.id)
                : [];
            setSelectedTypeIds(brandContentTypeIds);
        } catch (err) {
            setIsRmn(false);
            setSelectedTypeIds([]);
        }
    };

    const fetchAllData = async () => {
        setLoading(true);
        await Promise.all([fetchContentTypes(), fetchBrand()]);
        setLoading(false);
    };

    useEffect(() => {
        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandId]);

    // Store callback in ref to avoid dependency issues
    const onSelectionChangeRef = useRef(onSelectionChange);
    onSelectionChangeRef.current = onSelectionChange;

    useEffect(() => {
        if (onSelectionChangeRef.current) {
            onSelectionChangeRef.current(selectedTypeIds);
        }
    }, [selectedTypeIds]);

    const handleToggleContentType = (typeId: number) => {
        setSelectedTypeIds((prev) =>
            prev.includes(typeId)
                ? prev.filter((id) => id !== typeId)
                : [...prev, typeId],
        );
    };

    if (loading) {
        return <DotLottleLoader fill={false} />;
    }

    if (contentTypes.length === 0) {
        return (
            <Box py={4} textAlign="center">
                <Typography color="textSecondary">
                    No content types available.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
            {!isRmn && (
                <Box mb={2}>
                    <Typography color="error" fontWeight={600}>
                        This brand is not a Network. Content type selection is
                        disabled.
                    </Typography>
                </Box>
            )}
            {contentTypes.map((contentType) => (
                <Box
                    key={contentType.id}
                    sx={{
                        border: "1px solid var(--border)",
                        borderRadius: "8px",
                        px: 2,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={
                                    isRmn
                                        ? selectedTypeIds.includes(
                                              contentType.id,
                                          )
                                        : true
                                }
                                onChange={() =>
                                    isRmn &&
                                    handleToggleContentType(contentType.id)
                                }
                                disabled={!isRmn}
                            />
                        }
                        label=""
                        sx={{mr: 0}}
                    />
                    <Typography
                        fontWeight={600}
                        sx={{textTransform: "capitalize"}}
                    >
                        {contentType.name}
                    </Typography>
                    {contentType.expert_connect && (
                        <Chip
                            label="Portal Enabled"
                            size="small"
                            sx={{
                                backgroundColor: "var(--tint)",
                                color: "var(--accent-text)",
                                borderColor: "var(--accent)",
                                fontSize: "10px",
                                height: "20px",
                            }}
                            variant="outlined"
                        />
                    )}
                </Box>
            ))}
        </Box>
    );
}

export default BrandDetailContentTypesPanel;
